import React, { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { PageHeader } from './MassTimes';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';
import { HoneypotField } from '../components/HoneypotField';

const initialForm = {
  firstName: '', lastName: '', email: '', phone: '',
  address: '', city: '', postal: '',
  parish: 'st-marys',
  household: '1',
  childrenNames: '',
  baptized: 'yes',
  interests: [],
  envelopes: 'yes',
  notes: '',
  website: ''
};

const INTERESTS = [
  'Altar Serving', 'Lector / Reader', 'Music Ministry', 'Hospitality',
  "Catholic Women's League", 'Knights of Columbus', 'PREP / Catechism',
  'Bible Study', 'St. Vincent de Paul', 'Youth Group'
];

const NewParishioner = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const toggleInterest = (i) => {
    setForm(f => ({
      ...f,
      interests: f.interests.includes(i) ? f.interests.filter(x => x !== i) : [...f.interests, i]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createRegistration(form);
      setSubmitted(true);
      toast({ title: 'Welcome to All Saints!', description: 'Your registration has been received. We will be in touch soon.' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast({ title: 'Registration failed', description: err?.response?.data?.detail || 'Please check your entries and try again.', variant: 'destructive' });
    }
  };

  if (submitted) {
    return (
      <>
        <PageHeader eyebrow="Welcome" title="You&rsquo;re Registered!" subtitle="Thank you for making All Saints your parish home." />
        <section className="max-w-2xl mx-auto px-6 py-20 text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#f2ead6] text-[#3a2359] flex items-center justify-center mb-6">
            <CheckCircle2 size={32} />
          </div>
          <h2 className="font-display text-3xl text-[#2a1a45] mb-4">Blessings on your journey with us</h2>
          <p className="text-[#5a534a] leading-relaxed mb-6">
            A member of our parish team will contact you within a few days to welcome you personally, share information about our community, and answer any questions you may have.
          </p>
          <p className="text-sm text-[#78716c]">
            In the meantime, please introduce yourself to the pastor or a Welcome Minister after Mass this weekend.
          </p>
          <button onClick={() => { setSubmitted(false); setForm(initialForm); }} className="mt-8 text-sm underline text-[#3a2359]">Register another parishioner</button>
        </section>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="I'm New"
        title="New Parishioner Registration"
        subtitle="Welcome home! Please complete this form so we can get to know you and welcome you into our parish community."
      />

      <section className="max-w-3xl mx-auto px-6 py-14">
        <form onSubmit={handleSubmit} className="bg-white border border-[#e9e0cc] p-8 md:p-10 space-y-8">
          <HoneypotField value={form.website} onChange={(v) => setForm({ ...form, website: v })} />

          {/* Personal */}
          <fieldset>
            <legend className="font-display text-2xl text-[#2a1a45] mb-5">Your Information</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input label="First Name" value={form.firstName} onChange={v => setForm({...form, firstName: v})} required />
              <Input label="Last Name" value={form.lastName} onChange={v => setForm({...form, lastName: v})} required />
              <Input label="Email" type="email" value={form.email} onChange={v => setForm({...form, email: v})} required />
              <Input label="Phone" type="tel" value={form.phone} onChange={v => setForm({...form, phone: v})} required />
            </div>
          </fieldset>

          {/* Address */}
          <fieldset>
            <legend className="font-display text-2xl text-[#2a1a45] mb-5">Home Address</legend>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Input label="Street Address" value={form.address} onChange={v => setForm({...form, address: v})} required />
              </div>
              <Input label="City" value={form.city} onChange={v => setForm({...form, city: v})} required />
              <Input label="Postal Code" value={form.postal} onChange={v => setForm({...form, postal: v})} required />
            </div>
          </fieldset>

          {/* Parish preference */}
          <fieldset>
            <legend className="font-display text-2xl text-[#2a1a45] mb-5">Parish Community</legend>
            <p className="text-sm text-[#5a534a] mb-4">Which church do you primarily attend?</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                {v: 'st-marys', label: "St. Mary's (Ladysmith)"},
                {v: 'st-josephs', label: "St. Joseph's (Chemainus)"},
              ].map(o => (
                <label key={o.v} className={`flex items-center gap-3 p-4 border cursor-pointer ${form.parish === o.v ? 'border-[#3a2359] bg-[#f2ead6]/50' : 'border-[#e9e0cc]'}`}>
                  <input type="radio" name="parish" value={o.v} checked={form.parish === o.v} onChange={() => setForm({...form, parish: o.v})} className="accent-[#3a2359]" />
                  <span className="text-sm text-[#2a1a45]">{o.label}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Household */}
          <fieldset>
            <legend className="font-display text-2xl text-[#2a1a45] mb-5">Household</legend>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Number in Household</label>
                <select value={form.household} onChange={e => setForm({...form, household: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm">
                  {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={n}>{n}{n === 8 ? '+' : ''}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Baptized Catholic?</label>
                <select value={form.baptized} onChange={e => setForm({...form, baptized: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm">
                  <option value="yes">Yes</option>
                  <option value="partial">Some in household are</option>
                  <option value="no">No / Inquiring</option>
                </select>
              </div>
            </div>
            <Input label="Names & Ages of Children (if any)" value={form.childrenNames} onChange={v => setForm({...form, childrenNames: v})} placeholder="e.g. Anna (7), Michael (10)" />
          </fieldset>

          {/* Interests */}
          <fieldset>
            <legend className="font-display text-2xl text-[#2a1a45] mb-5">Ministries of Interest</legend>
            <p className="text-sm text-[#5a534a] mb-4">Check any that interest you — no commitment required.</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {INTERESTS.map(i => (
                <label key={i} className="flex items-center gap-3 p-3 border border-[#e9e0cc] hover:border-[#3a2359] cursor-pointer">
                  <input type="checkbox" checked={form.interests.includes(i)} onChange={() => toggleInterest(i)} className="accent-[#3a2359]" />
                  <span className="text-sm text-[#2a1a45]">{i}</span>
                </label>
              ))}
            </div>
          </fieldset>

          {/* Envelopes & Notes */}
          <fieldset>
            <legend className="font-display text-2xl text-[#2a1a45] mb-5">Additional</legend>
            <div className="mb-4">
              <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Would you like offering envelopes?</label>
              <select value={form.envelopes} onChange={e => setForm({...form, envelopes: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm">
                <option value="yes">Yes, please</option>
                <option value="no">No thank you</option>
                <option value="electronic">I&rsquo;ll use pre-authorized giving</option>
              </select>
            </div>
            <div>
              <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">Anything else you&rsquo;d like us to know?</label>
              <textarea rows={4} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm resize-none" />
            </div>
          </fieldset>

          <button type="submit" className="w-full px-6 py-4 bg-[#3a2359] text-white text-sm tracking-wide hover:bg-[#4d2f75]">
            Submit Registration
          </button>
          <p className="text-xs text-[#78716c] text-center">Your information is kept private and used only for parish communications.</p>
        </form>
      </section>
    </>
  );
};

const Input = ({ label, value, onChange, type = 'text', required, placeholder }) => (
  <div>
    <label className="block text-xs tracking-wider uppercase text-[#5a534a] mb-2">{label}{required && <span className="text-[#a68a5b]"> *</span>}</label>
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-3 border border-[#e9e0cc] bg-[#faf7f0] focus:border-[#3a2359] focus:outline-none text-sm"
    />
  </div>
);

export default NewParishioner;
